import { useState, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useSettings } from './useSettings'
import { useBrokerConnection } from './useBrokerConnection'
import {
  splitGroupHasPartialFailure,
  findSuccessfulOrphanSiblings,
} from '../lib/bracketOrderGroups.js'
import {
  acceptedOrderResults,
  buildOrderBatchNotification,
  isVerifiedBrokerOrderStatus,
} from '../lib/orderReconciliation.js'
import { useNotifications } from './useNotifications.jsx'
import { readOrderBatchResponse } from '../lib/orderBatchResponse.js'

const FETCH_TIMEOUT_MS = 30000
const STAGED_SETUP_RECOVERY_LOOKBACK_MS = 2 * 60 * 1000
const BROKER_CONFIRMATION_MAX_ATTEMPTS = 3
const BROKER_CONFIRMATION_RETRY_DELAY_MS = 25
const ORDER_RECONCILIATION_MAX_ATTEMPTS = 4
const ORDER_RECONCILIATION_RETRY_DELAY_MS =
  typeof process !== 'undefined' && process.env?.NODE_ENV === 'test' ? 1 : 1000
const BROKER_TERMINAL_ORDER_STATUSES = new Set(['FILLED', 'CANCELED', 'CANCELLED', 'EXPIRED', 'REJECTED', 'REPLACED'])
const SERVER_BACKED_SUCCESS_ACTIONS = new Set(['STAGE_ORDERS', 'ADD_TRANCHE'])

// Map UI display actions to OMS API actions
const OMS_ACTION_MAP = {
  STAGE_ORDERS: 'PLACE',
  ADD_TRANCHE: 'PLACE',
  // Batched actions (TAKE_PROFIT, FLATTEN) already use correct per-batch actions
}

// EF-190: actions whose leading batches are preconditions for the following
// PLACE batches. If any precondition batch shows a per-order or whole-batch
// failure, every subsequent batch in the same payload must be skipped and
// flagged so the user sees per-row failure status explaining the gap.
// Keyed by pendingPayload.action → batch.action values that are preconditions.
//
// REPLACE_SETUP (Edit + Replace, EF-193) and FLATTEN (Close Full Position):
//   precondition = CANCEL. Skip rows carry BOTH skippedDueToPreconditionFailure
//   and the legacy skippedDueToCancelFailure flag — the latter preserves
//   WorkingSetupCard.jsx's existing banner + filter logic from EF-193.
// TAKE_PROFIT (Take 1/3 Profit):
//   preconditions = CANCEL (remove existing targets/depleted stop legs) and
//   REPLACE (reduce stop quantity before the market sell).
//   Skip rows carry skippedDueToPreconditionFailure only — no existing
//   consumer expects skippedDueToCancelFailure on TAKE_PROFIT, and the
//   legacy cancel flag is reserved for REPLACE_SETUP + FLATTEN.
const PRECONDITION_BATCHES_FOR_ACTION = {
  REPLACE_SETUP: new Set(['CANCEL']),
  REPAIR_SETUP: new Set(['CANCEL']),
  FLATTEN: new Set(['CANCEL']),
  TAKE_PROFIT: new Set(['CANCEL', 'REPLACE']),
}

// Schwab Trader API spec:
// https://developer.schwab.com/products/trader-api--individual/details/specifications/Retail%20Trader%20API%20Production
// Cancel and replace are order-resource mutations; EF-196 treats the mutation
// response as an ack only and confirms broker state via refresh-position before
// allowing dependent market sells or accepting a replacement stop as complete.
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function requiresBrokerConfirmation(orders) {
  return Array.isArray(orders) && orders.some(order => order?.requiresBrokerConfirmation === true)
}

function normalizeOrderId(value) {
  return String(value ?? '').trim()
}

function normalizeText(value) {
  return String(value ?? '').trim().toUpperCase()
}

function isRetryableStaleIntentQuoteFailure(row) {
  return row?.success !== true
    && !normalizeOrderId(row?.orderId)
    && String(row?.error || '').startsWith('Current quote is stale for buy entry order-intent validation')
}

function brokerOrderLeg(order) {
  return order?.orderLegCollection?.[0] || {}
}

function brokerOrderSymbol(order) {
  return normalizeText(order?.symbol || brokerOrderLeg(order)?.instrument?.symbol)
}

function brokerOrderInstruction(order) {
  return normalizeText(order?.side || order?.instruction || brokerOrderLeg(order)?.instruction)
}

function brokerOrderEnteredMs(order) {
  const raw = order?.enteredTime || order?.entered_time || order?.closeTime || order?.close_time
  const parsed = Date.parse(raw)
  return Number.isFinite(parsed) ? parsed : null
}

function expectedBrokerEntryOrderType(order) {
  const type = normalizeText(order?.orderType)
  if (type !== 'BRACKET_ENTRY') return type
  return order?.entryType === 'On Strength' ? 'STOP' : 'LIMIT'
}

function expectedBrokerEntryPrice(order) {
  const type = expectedBrokerEntryOrderType(order)
  if (type === 'STOP' || type === 'STOP_LIMIT') return Number(order?.price ?? order?.stopPrice)
  if (type === 'LIMIT') return Number(order?.price)
  return null
}

function numericEqual(left, right, epsilon = 0.01) {
  const a = Number(left)
  const b = Number(right)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false
  return Math.abs(a - b) <= epsilon
}

function brokerOrderEntryPrice(order, expectedType) {
  if (expectedType === 'STOP' || expectedType === 'STOP_LIMIT') {
    return order?.stopPrice ?? order?.entryPrice
  }
  if (expectedType === 'LIMIT') {
    return order?.price ?? order?.entryPrice
  }
  return order?.price ?? order?.stopPrice ?? order?.entryPrice
}

function hasExpectedTargetChild(brokerOrder, requestOrder) {
  const expectedTarget = Number(requestOrder?.targetPrice)
  const expectedTargetQty = Number(requestOrder?.targetQuantity)
  if (!Number.isFinite(expectedTarget) || expectedTarget <= 0 || !Number.isFinite(expectedTargetQty) || expectedTargetQty <= 0) {
    return true
  }
  const children = brokerOrder?.childOrders || []
  return children.some(child =>
    normalizeText(child?.orderType) === 'LIMIT' &&
    brokerOrderInstruction(child).includes('SELL') &&
    numericEqual(child?.price, expectedTarget) &&
    numericEqual(child?.quantity, expectedTargetQty, 0)
  )
}

function isTimedOutSubmitRow(row) {
  return row?.success !== true &&
    !normalizeOrderId(row?.orderId) &&
    (row?.submissionUncertain === true || String(row?.error || '').toLowerCase().includes('request timed out'))
}

function brokerOrderMatchesSubmittedSetupOrder(brokerOrder, requestOrder, submittedAtMs) {
  if (!brokerOrder || !requestOrder) return false
  if (!isVerifiedBrokerOrderStatus(brokerOrder.status)) return false

  const enteredMs = brokerOrderEnteredMs(brokerOrder)
  if (enteredMs == null || enteredMs < submittedAtMs - STAGED_SETUP_RECOVERY_LOOKBACK_MS) return false

  const accountNumber = String(brokerOrder?.accountNumber ?? brokerOrder?.account_number ?? '')
  if (accountNumber !== String(requestOrder.account_number ?? '')) return false
  if (brokerOrderSymbol(brokerOrder) !== normalizeText(requestOrder.symbol)) return false

  const expectedInstruction = normalizeText(requestOrder.side)
  if (!brokerOrderInstruction(brokerOrder).includes(expectedInstruction)) return false

  const expectedType = expectedBrokerEntryOrderType(requestOrder)
  if (normalizeText(brokerOrder.orderType) !== expectedType) return false
  if (!numericEqual(brokerOrder.quantity ?? brokerOrder.remainingQuantity, requestOrder.quantity, 0)) return false
  if (!numericEqual(brokerOrderEntryPrice(brokerOrder, expectedType), expectedBrokerEntryPrice(requestOrder))) return false
  if (!hasExpectedTargetChild(brokerOrder, requestOrder)) return false

  return true
}

function recoverTimedOutSetupRowsFromBroker({
  orders,
  results,
  brokerOrders,
  submittedAtMs,
  recoveryBatchAction,
}) {
  if (!Array.isArray(orders) || !Array.isArray(results) || !Array.isArray(brokerOrders)) return results
  const sortedBrokerOrders = [...brokerOrders].sort((a, b) => (brokerOrderEnteredMs(a) || 0) - (brokerOrderEnteredMs(b) || 0))
  // Never assign an already acknowledged order to an unresolved sibling.
  const usedOrderIds = new Set(results.map(row => normalizeOrderId(row?.orderId)).filter(Boolean))

  return results.map((row, index) => {
    if (!isTimedOutSubmitRow(row)) return row
    const requestOrder = orders[index]
    const brokerOrder = sortedBrokerOrders.find(candidate => {
      const id = normalizeOrderId(candidate?.orderId)
      return id && !usedOrderIds.has(id) &&
        brokerOrderMatchesSubmittedSetupOrder(candidate, requestOrder, submittedAtMs)
    })
    if (!brokerOrder) return row

    const orderId = normalizeOrderId(brokerOrder.orderId)
    usedOrderIds.add(orderId)
    return {
      ...row,
      success: true,
      orderId,
      error: null,
      message: 'Verified at Schwab after client timeout',
      recoveredFromTimeout: true,
      ...(row.submissionUncertain && { submissionUncertain: false }),
      gatewayBatchAction: row?.gatewayBatchAction ?? recoveryBatchAction,
    }
  })
}

function findBrokerOrder(refreshedOrders, accountNumber, orderId) {
  const id = normalizeOrderId(orderId)
  const acct = String(accountNumber ?? '')
  if (!id || !acct || !Array.isArray(refreshedOrders)) return null
  return refreshedOrders.find(order =>
    String(order?.accountNumber ?? order?.account_number ?? '') === acct &&
    normalizeOrderId(order?.orderId) === id
  ) || null
}

function acceptedOrdersVisibleAtBroker(accepted, brokerOrders) {
  if (!Array.isArray(accepted) || accepted.length === 0) return true
  return accepted.every(row =>
    findBrokerOrder(brokerOrders, row.account_number ?? row.accountNumber, row.orderId)
  )
}

function shouldUseServerBackedSuccessNotification(action, results, accepted) {
  const normalizedAction = String(action || '').toUpperCase()
  if (!SERVER_BACKED_SUCCESS_ACTIONS.has(normalizedAction)) return false
  if (!Array.isArray(results) || results.length === 0) return false
  if (!Array.isArray(accepted) || accepted.length !== results.length) return false
  return results.every(row => row?.success === true && normalizeOrderId(row.orderId))
}

function getBrokerPositionQuantity(refreshedPositions, accountNumber) {
  const acct = String(accountNumber ?? '')
  if (!acct || !Array.isArray(refreshedPositions)) return null
  const row = refreshedPositions.find(position => String(position?.accountNumber ?? position?.account_number ?? '') === acct)
  if (!row) return 0
  return Number(row.longQuantity ?? row.quantity ?? 0) || 0
}

function isTerminalBrokerOrder(order) {
  if (!order) return true
  return BROKER_TERMINAL_ORDER_STATUSES.has(String(order.status ?? '').toUpperCase())
}

function dependentSellQuantityByAccount(remainingBatches) {
  const quantities = new Map()
  for (const batch of remainingBatches || []) {
    if (batch?.action !== 'PLACE') continue
    for (const order of batch.orders || []) {
      if (String(order?.side ?? '').toUpperCase() !== 'SELL') continue
      if (String(order?.orderType ?? '').toUpperCase() !== 'MARKET') continue
      const accountNumber = String(order.account_number ?? '')
      if (!accountNumber) continue
      quantities.set(accountNumber, (quantities.get(accountNumber) || 0) + (Number(order.quantity) || 0))
    }
  }
  return quantities
}

function applyBrokerConfirmation({ batchAction, orders, results, refreshed, remainingBatches = [] }) {
  if (!requiresBrokerConfirmation(orders)) return { results, confirmed: true, retryable: false }

  const nextResults = results.map(result => ({ ...result }))
  const expectedSellByAccount = dependentSellQuantityByAccount(remainingBatches)
  let confirmed = true
  let retryable = false

  orders.forEach((order, idx) => {
    if (order?.requiresBrokerConfirmation !== true) return
    const result = nextResults[idx]
    if (result?.success !== true) return

    const accountNumber = String(order.account_number ?? '')
    const refreshedOrders = refreshed?.orders || []
    const refreshedPositions = refreshed?.positions || []

    if (batchAction === 'CANCEL') {
      const liveOrder = findBrokerOrder(refreshedOrders, accountNumber, order.existingOrderId)
      const expectedSellQuantity = expectedSellByAccount.get(accountNumber)
      const positionQuantity = getBrokerPositionQuantity(refreshedPositions, accountNumber)
      const positionChangedMidCancel =
        expectedSellQuantity != null &&
        positionQuantity != null &&
        positionQuantity < expectedSellQuantity

      if (positionChangedMidCancel) {
        nextResults[idx] = {
          ...result,
          success: false,
          error: `Broker confirmation failed: ${order.symbol} position quantity changed before sell`,
          brokerConfirmationFailed: true,
        }
        confirmed = false
        return
      }

      if (liveOrder && !isTerminalBrokerOrder(liveOrder)) {
        nextResults[idx] = {
          ...result,
          success: false,
          error: `Broker confirmation failed: cancel ${order.existingOrderId} is still live at Schwab`,
          brokerConfirmationFailed: true,
        }
        confirmed = false
        retryable = true
      }
      return
    }

    if (batchAction === 'REPLACE') {
      const oldOrder = findBrokerOrder(refreshedOrders, accountNumber, order.existingOrderId)
      const newOrderId = result.orderId
      const newOrder = findBrokerOrder(refreshedOrders, accountNumber, newOrderId)
      if ((oldOrder && !isTerminalBrokerOrder(oldOrder)) || !newOrderId || !newOrder || isTerminalBrokerOrder(newOrder)) {
        nextResults[idx] = {
          ...result,
          success: false,
          error: `Broker confirmation failed: replacement stop for ${order.symbol} was not confirmed`,
          brokerConfirmationFailed: true,
        }
        confirmed = false
        retryable = true
      }
    }
  })

  return { results: nextResults, confirmed, retryable }
}

// EF-189: split-bracket metadata travels with the request payload (bracketRole
// 'TARGET' / 'RUNNER' / 'ENTRY' and a shared bracketGroupId for split pairs).
// Merge it back onto each per-order result so callers can persist the role and
// the group id alongside the placed orderId. Symbol is also propagated from
// the request so the v3 rollback path (findSuccessfulOrphanSiblings →
// cancelPayload) can build the cancel request without re-deriving symbol from
// scratch. The OMS preserves request order; account_number is used as a
// sanity fallback when index alignment is lost.
function mergeBracketMetaIntoResults(requests, results) {
  if (!Array.isArray(results)) return results
  if (!Array.isArray(requests) || requests.length === 0) return results
  return results.map((r, i) => {
    // EF-189 v3 amend: strict positional merge. Same-account multi-order
    // batches (a split bracket: TARGET + RUNNER on one account) require
    // deterministic 1:1 mapping; an account-only fallback could cross-wire
    // tranche roles if results came back out of order. The OMS preserves
    // request order, so positional is both sufficient and safer.
    const req = requests[i]
    if (!req) return r
    const meta = {}
    if (req.bracketRole !== undefined) meta.bracketRole = req.bracketRole
    if (req.bracketGroupId !== undefined) meta.bracketGroupId = req.bracketGroupId
    // EF-189 v3: only propagate symbol for split-bracket results (those that
    // carry a bracketGroupId) so the rollback path can build the CANCEL
    // payload without re-deriving symbol. Avoid broadening the shape of
    // single-order results that don't need it.
    if (
      req.bracketGroupId !== undefined &&
      req.symbol !== undefined &&
      (r == null || r.symbol === undefined)
    ) {
      meta.symbol = req.symbol
    }
    return Object.keys(meta).length === 0 ? r : { ...r, ...meta }
  })
}

export function useOrderGateway({ onOrdersChanged, biometricGate } = {}) {
  const { settings } = useSettings()
  // OTR-04: OMS gate moved from useAutoSyncContext (tokenWarning/syncError)
  // to useBrokerConnection. tokenWarning is still consumed elsewhere
  // (Layout banner, SetupStagingPanel inline status) — those keep their
  // independent useAutoSyncContext consumption.
  const brokerConn = useBrokerConnection('schwab')
  // OTR-04: gate on user-action-required states only. requiresReauth is the
  // 7-day-refresh-token-dead case; no_connection means the user never
  // finished OAuth. stale_access_token is recoverable internally —
  // withBrokerAuth refreshes on entry — so gating there would block a click
  // that would otherwise succeed.
  const schwabAuthExpired = brokerConn.requiresReauth || brokerConn.reason === 'no_connection'
  const omsEnabled = (settings?.oms_enabled || false) && !schwabAuthExpired
  const abortRef = useRef(null)
  const didUserCancelRef = useRef(false)
  const {
    enabled: orderNotificationsEnabled,
    upsertNotification,
  } = useNotifications()

  const [confirmState, setConfirmState] = useState({
    open: false,
    action: null,
    symbol: null,
    orders: [],
    loading: false,
    results: null,
  })

  const [pendingPayload, setPendingPayload] = useState(null)

  const reconcileOrderBatch = useCallback(async ({ batchId, symbol, action, results }) => {
    const accepted = acceptedOrderResults(results)
    if (!orderNotificationsEnabled) return
    if (shouldUseServerBackedSuccessNotification(action, results, accepted)) return
    if (accepted.length === 0) {
      if (Array.isArray(results) && results.some(row => row?.success !== true)) {
        upsertNotification(buildOrderBatchNotification({
          id: batchId,
          symbol,
          action,
          results,
          brokerOrders: [],
        }))
      }
      return
    }

    const accounts = [...new Set(accepted.map(r => String(r.account_number ?? r.accountNumber ?? '')).filter(Boolean))]
    const orderIds = [...new Set(accepted.map(r => String(r.orderId ?? '')).filter(Boolean))]
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not authenticated')

      const params = new URLSearchParams({
        includeTerminal: '1',
        orderIds: orderIds.join(','),
      })
      const orderRefs = accepted
        .map(r => {
          const accountNumber = String(r.account_number ?? r.accountNumber ?? '').trim()
          const orderId = String(r.orderId ?? '').trim()
          return accountNumber && orderId ? `${accountNumber}:${orderId}` : null
        })
        .filter(Boolean)
      if (orderRefs.length > 0) params.set('orderRefs', orderRefs.join(','))
      if (accounts.length > 0) params.set('accounts', accounts.join(','))

      let body = { orders: [] }
      for (let attempt = 1; attempt <= ORDER_RECONCILIATION_MAX_ATTEMPTS; attempt += 1) {
        const response = await fetch(`/api/schwab/orders?${params}`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
          signal: controller.signal,
        })
        if (!response.ok) throw new Error(`Schwab reconciliation failed: ${response.status}`)
        body = await response.json()
        if (acceptedOrdersVisibleAtBroker(accepted, body.orders || [])) break
        if (attempt < ORDER_RECONCILIATION_MAX_ATTEMPTS) {
          await sleep(ORDER_RECONCILIATION_RETRY_DELAY_MS)
        }
      }
      upsertNotification(buildOrderBatchNotification({
        id: batchId,
        symbol,
        action,
        results,
        brokerOrders: body.orders || [],
      }))
    } catch (err) {
      upsertNotification(buildOrderBatchNotification({
        id: batchId,
        symbol,
        action,
        results,
        brokerOrders: [],
        error: err?.name === 'AbortError' ? 'Schwab reconciliation timed out' : err.message,
      }))
    } finally {
      clearTimeout(timeoutId)
    }
  }, [orderNotificationsEnabled, upsertNotification])

  /**
   * Show the confirmation modal with order details.
   * @param {string} action - 'PLACE' | 'REPLACE' | 'CANCEL' | 'TAKE_PROFIT'
   * @param {string} symbol
   * @param {Array} orderSummaries - UI-friendly order details for the modal
   * @param {Array|null} gatewayOrders - Single-action orders, or null if using batches
   * @param {Array|null} batches - [{action, orders}] for multi-leg operations
   */
  const showConfirmation = useCallback((action, symbol, orderSummaries, gatewayOrders, batches = null) => {
    setConfirmState({
      open: true,
      action,
      symbol,
      orders: orderSummaries,
      loading: false,
      results: null,
    })
    // EF-193: preserve the top-level action for batched payloads. Without
    // this, the REPLACE_SETUP fail-closed guard below cannot distinguish a
    // user-initiated Edit (which must NOT place fresh orders after a cancel
    // failure) from a TAKE_PROFIT / FLATTEN batched flow (which has different
    // semantics and must continue across partial-batch failures).
    if (batches) {
      setPendingPayload({ action, symbol, batches })
    } else {
      setPendingPayload({ action, symbol, orders: gatewayOrders })
    }
  }, [])

  /**
   * Confirm and execute orders. Accepts optional selectedAccounts set to filter which accounts to execute.
   * @param {Set|null} selectedAccounts - Account numbers to include. If null/undefined, all accounts execute.
   */
  const confirmOrder = useCallback(async (selectedAccounts) => {
    if (!pendingPayload) return
    didUserCancelRef.current = false

    // Filter payload to only selected accounts. Keep this ahead of every
    // failure-synthesis path so UI rows, notifications, and onOrdersChanged
    // reflect exactly the orders the user submitted.
    const filterOrders = (orders) => {
      if (!selectedAccounts || selectedAccounts.size === 0) return orders
      return orders.filter(o => selectedAccounts.has(o.account_number))
    }
    const fallbackOrders = pendingPayload.batches
      ? pendingPayload.batches.flatMap(b => filterOrders(b.orders || []))
      : filterOrders(pendingPayload.orders || [])

    // Biometric gate — verify before executing any destructive action
    if (biometricGate) {
      setConfirmState(prev => ({ ...prev, loading: true }))
      try {
        const { verified, error: bioError } = await biometricGate.verify()
        if (!verified) {
          setConfirmState(prev => ({
            ...prev,
            loading: false,
            results: fallbackOrders.map(o => ({
              account_number: o.account_number,
              success: false,
              error: bioError || 'Biometric verification required',
            })),
          }))
          return
        }
      } catch (bioErr) {
        setConfirmState(prev => ({
          ...prev,
          loading: false,
          results: fallbackOrders.map(o => ({
            account_number: o.account_number,
            success: false,
            error: bioErr.message || 'Biometric verification failed',
          })),
        }))
        return
      }
    }

    setConfirmState(prev => ({ ...prev, loading: true }))

    // Default controller for dependent action flows. Setup entry batches own
    // a separate deadline and reconcile ambiguous timeouts before remediation.
    const controller = new AbortController()
    abortRef.current = controller
    let timeoutId = null
    const ensureAggregateTimeout = () => {
      if (timeoutId == null) {
        timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
      }
    }

    try {
      // EF-182: silent-retry envelope. If the OMS gateway returns
      // SESSION_EXPIRED (an expired Supabase *browser* session — NOT a
      // Schwab auth failure), refresh the Supabase session once and re-send
      // the exact same payload. Returns the final response data (results or
      // error). Never retries more than once. Never retries on
      // SCHWAB_AUTH_EXPIRED (different remediation path — see the
      // OrderConfirmationModal banner split in EF-182 Section D).
      const sendWithSessionRetry = async (bodyJson, signal = controller.signal, onResult) => {
        const doFetch = async (accessToken) => {
          const res = await fetch('/api/oms/execute', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(bodyJson),
            signal,
          })
          return readOrderBatchResponse(res, onResult)
        }

        const initialSession = (await supabase.auth.getSession()).data?.session
        if (!initialSession?.access_token) throw new Error('Not authenticated')

        const first = await doFetch(initialSession.access_token)
        if (first?.errorCode !== 'SESSION_EXPIRED') return first

        const { data, error } = await supabase.auth.refreshSession()
        if (error || !data?.session?.access_token) {
          return {
            error: error?.message || 'Session refresh failed',
            errorCode: 'SESSION_EXPIRED',
          }
        }
        // EF-182 Grok validation: the retry must NOT reuse the outer
        // `controller` (it has already had a chance to abort during the
        // first fetch + refreshSession round-trip). Use a fresh,
        // short-lived AbortController scoped to the retry only.
        const retryController = new AbortController()
        const retryTimeoutId = setTimeout(() => retryController.abort(), FETCH_TIMEOUT_MS)
        const abortSetupRetry = () => retryController.abort()
        if (bodyJson.submissionMode === 'setup') {
          signal.addEventListener('abort', abortSetupRetry, { once: true })
          if (signal.aborted) retryController.abort()
        }
        try {
          const res = await fetch('/api/oms/execute', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${data.session.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(bodyJson),
            signal: retryController.signal,
          })
          return await readOrderBatchResponse(res, onResult)
        } finally {
          clearTimeout(retryTimeoutId)
          signal.removeEventListener('abort', abortSetupRetry)
        }
      }

      const submitStagedSetupOrders = async (orders, action) => {
        const setupController = new AbortController()
        abortRef.current = setupController
        const setupTimeoutId = setTimeout(() => setupController.abort(), FETCH_TIMEOUT_MS)
        const received = new Map()
        const onResult = (index, row) => {
          if (index < orders.length) received.set(index, row)
        }
        try {
          const payload = { action, submissionMode: 'setup', streamResults: true, orders }
          let data = await sendWithSessionRetry(payload, setupController.signal, onResult)
          // Only a complete, explicitly pre-broker rejection may be retried.
          // A missing/ambiguous response or any accepted row is NEVER replayed.
          if (received.size === 0 && data?.preflightBlocked === true &&
              Array.isArray(data.results) && data.results.length === orders.length &&
              data.results.every(row => row?.success === false && !normalizeOrderId(row.orderId)) &&
              data.results.some(isRetryableStaleIntentQuoteFailure)) {
            data = await sendWithSessionRetry(payload, setupController.signal, onResult)
          }
          return mergeBracketMetaIntoResults(orders, orders.map((order, index) =>
            data?.results?.[index] || received.get(index) || {
              account_number: order.account_number,
              success: false,
              error: data?.error || 'Order result unavailable — check broker state before retrying',
              ...(!data?.error && { submissionUncertain: true }),
              ...(data?.errorCode && { errorCode: data.errorCode }),
            }
          ))
        } catch (err) {
          if (err?.name === 'AbortError' && didUserCancelRef.current && received.size === 0) throw err
          return mergeBracketMetaIntoResults(orders, orders.map((order, index) => received.get(index) || ({
            account_number: order.account_number,
            success: false,
            error: err?.name === 'AbortError' ? 'Request timed out (30s)' : err.message,
            ...(err?.name !== 'AbortError' && { submissionUncertain: true }),
          })))
        } finally {
          clearTimeout(setupTimeoutId)
          abortRef.current = null
        }
      }

      const recoverTimedOutSetupOrders = async ({ orders, results, submittedAtMs, recoveryBatchAction }) => {
        if (!Array.isArray(results) || !results.some(isTimedOutSubmitRow)) return results
        const accounts = [...new Set((orders || [])
          .map(order => String(order?.account_number ?? '').trim())
          .filter(Boolean))]
        if (accounts.length === 0) return results

        const recoveryController = new AbortController()
        const recoveryTimeoutId = setTimeout(() => recoveryController.abort(), FETCH_TIMEOUT_MS)
        try {
          const { data: { session } } = await supabase.auth.getSession()
          if (!session?.access_token) return results

          const params = new URLSearchParams({
            includeTerminal: '1',
            accounts: accounts.join(','),
          })
          const response = await fetch(`/api/schwab/orders?${params}`, {
            headers: { Authorization: `Bearer ${session.access_token}` },
            signal: recoveryController.signal,
          })
          if (!response.ok) return results
          const body = await response.json()
          return recoverTimedOutSetupRowsFromBroker({
            orders,
            results,
            brokerOrders: body.orders || [],
            submittedAtMs,
            recoveryBatchAction,
          })
        } catch {
          return results
        } finally {
          clearTimeout(recoveryTimeoutId)
        }
      }

      const refreshBrokerState = async (symbol, accounts) => {
        const initialSession = (await supabase.auth.getSession()).data?.session
        if (!initialSession?.access_token) throw new Error('Not authenticated')

        let url = `/api/schwab/refresh-position?symbol=${encodeURIComponent(symbol)}`
        if (accounts?.length > 0) url += `&accounts=${accounts.map(encodeURIComponent).join(',')}`

        const doFetch = async (accessToken) => {
          const res = await fetch(url, {
            headers: { Authorization: `Bearer ${accessToken}` },
            signal: controller.signal,
          })
          if (res.ok === false) return { error: `Failed to refresh broker state: ${res.status}` }
          return res.json()
        }

        const first = await doFetch(initialSession.access_token)
        if (first?.errorCode !== 'SESSION_EXPIRED') return first

        const { data, error } = await supabase.auth.refreshSession()
        if (error || !data?.session?.access_token) {
          return {
            error: error?.message || 'Session refresh failed',
            errorCode: 'SESSION_EXPIRED',
          }
        }
        return doFetch(data.session.access_token)
      }

      const confirmBrokerState = async ({ batchAction, orders, results, remainingBatches = [] }) => {
        if (!requiresBrokerConfirmation(orders)) return results
        const symbolForRefresh = pendingPayload.symbol || orders.find(o => o?.symbol)?.symbol
        if (!symbolForRefresh) {
          return results.map(result => result?.success === true
            ? { ...result, success: false, error: 'Broker confirmation failed: missing symbol', brokerConfirmationFailed: true }
            : result)
        }
        const accounts = [...new Set(orders
          .filter(order => order?.requiresBrokerConfirmation === true)
          .map(order => String(order.account_number ?? ''))
          .filter(Boolean))]

        let latest = results
        for (let attempt = 1; attempt <= BROKER_CONFIRMATION_MAX_ATTEMPTS; attempt += 1) {
          const refreshed = await refreshBrokerState(symbolForRefresh, accounts)
          if (refreshed?.error) {
            return latest.map(result => result?.success === true
              ? { ...result, success: false, error: refreshed.error, brokerConfirmationFailed: true, ...(refreshed.errorCode && { errorCode: refreshed.errorCode }) }
              : result)
          }

          const confirmation = applyBrokerConfirmation({
            batchAction,
            orders,
            results,
            refreshed,
            remainingBatches,
          })
          latest = confirmation.results
          if (confirmation.confirmed || !confirmation.retryable || attempt === BROKER_CONFIRMATION_MAX_ATTEMPTS) {
            return latest
          }
          await sleep(BROKER_CONFIRMATION_RETRY_DELAY_MS)
        }
        return latest
      }

      let allResults = []

      if (pendingPayload.batches) {
        const buildSkippedRowsForRemainingBatches = (remainingBatches, failedBatchAction) => {
          const rows = []
          for (const remaining of remainingBatches) {
            const filteredRemaining = filterOrders(remaining.orders)
            const legacyCancelFlag =
              (pendingPayload.action === 'REPLACE_SETUP' || pendingPayload.action === 'FLATTEN') &&
              failedBatchAction === 'CANCEL'
              ? { skippedDueToCancelFailure: true }
              : {}
            const skipRows = filteredRemaining.map(o => ({
              account_number: o.account_number,
              success: false,
              error: `Precondition ${failedBatchAction} batch failed — subsequent ${remaining.action} batch not executed`,
              skippedDueToPreconditionFailure: true,
              ...legacyCancelFlag,
              gatewayBatchAction: remaining.action,
            }))
            rows.push(...mergeBracketMetaIntoResults(filteredRemaining, skipRows))
          }
          return rows
        }

        // Multi-leg: send each batch sequentially, filtered by selected accounts.
        // EF-193: tag every merged result with its originating batch.action so
        // the consumer can distinguish CANCEL rows from PLACE rows on the same
        // account (Edit must NOT persist canceled orderIds as new live tracking).
        for (const batch of pendingPayload.batches) {
          const filteredOrders = filterOrders(batch.orders)
          if (filteredOrders.length === 0) continue
          ensureAggregateTimeout()
          const remainingBatches = pendingPayload.batches.slice(
            pendingPayload.batches.indexOf(batch) + 1
          )
          const preconditionActions = PRECONDITION_BATCHES_FOR_ACTION[pendingPayload.action]
          const batchIsPrecondition = preconditionActions?.has(batch.action) === true
          const submittedAtMs = Date.now()
          let data
          try {
            data = await sendWithSessionRetry({ action: batch.action, orders: filteredOrders })
          } catch (err) {
            if (err?.name === 'AbortError' && didUserCancelRef.current) throw err
            const errorMsg = err?.name === 'AbortError' ? 'Request timed out (30s)' : (err?.message || 'Unknown error')
            let merged = mergeBracketMetaIntoResults(
              filteredOrders,
              filteredOrders.map(o => ({
                account_number: o.account_number,
                success: false,
                error: errorMsg,
                gatewayBatchAction: batch.action,
              }))
            )
            if (batch.action === 'PLACE') {
              merged = await recoverTimedOutSetupOrders({
                orders: filteredOrders,
                results: merged,
                submittedAtMs,
                recoveryBatchAction: batch.action,
              })
            }
            allResults = allResults.concat(merged)
            const batchHadFailure = filteredOrders.some(
              (_, idx) => merged?.[idx]?.success !== true
            )
            if (batchIsPrecondition && batchHadFailure) {
              allResults = allResults.concat(
                buildSkippedRowsForRemainingBatches(remainingBatches, batch.action)
              )
              break
            }
            if (batchHadFailure) break
            continue
          }
          if (data.results) {
            let merged = mergeBracketMetaIntoResults(filteredOrders, data.results)
              .map(r => ({ ...r, gatewayBatchAction: batch.action }))
            merged = await confirmBrokerState({
              batchAction: batch.action,
              orders: filteredOrders,
              results: merged,
              remainingBatches,
            })
            allResults = allResults.concat(merged)
            // EF-193 1c (now EF-190 generalized): precondition fail-closed
            // guard. If any batch in a precondition-mapped action
            // (PRECONDITION_BATCHES_FOR_ACTION) shows ANY per-order failure,
            // every subsequent batch must be skipped — running a market
            // sell on top of a failed stop-reduction (TAKE_PROFIT) or a
            // failed stop-cancel (FLATTEN) is exactly the oversell hazard
            // EF-190 was opened to close.
            //
            // EF-193 post-Codex hardening (preserved): check by REQUEST
            // length, not results length. A short/malformed OMS response
            // (fewer result rows than requested orders) used to slip
            // through `data.results.some(r => r.success !== true)` because
            // missing rows looked like implicit success. Iterate over
            // filteredOrders so any positional gap counts as a failure.
            //
            // EF-190: the legacy `skippedDueToCancelFailure: true` flag is
            // propagated ONLY when the precondition is CANCEL
            // (REPLACE_SETUP + FLATTEN). EF-193's WorkingSetupCard banner
            // (WorkingSetupCard.jsx:706) and EF-193's filter logic at
            // lines 168/180 are keyed on that exact flag — keeping it on
            // CANCEL-precondition paths preserves backward compatibility.
            // TAKE_PROFIT skip rows carry only the canonical
            // skippedDueToPreconditionFailure marker.
            const batchHadFailure = filteredOrders.some(
              (_, idx) => merged?.[idx]?.success !== true
            )
            if (batchIsPrecondition && batchHadFailure) {
              allResults = allResults.concat(
                buildSkippedRowsForRemainingBatches(remainingBatches, batch.action)
              )
              break
            }
          } else if (data.error) {
            const failureRows = filteredOrders.map(o => ({
              account_number: o.account_number,
              success: false,
              error: data.error,
              ...(data.errorCode && { errorCode: data.errorCode }),
              gatewayBatchAction: batch.action,
            }))
            allResults = allResults.concat(
              mergeBracketMetaIntoResults(filteredOrders, failureRows)
            )
            // EF-193 1b / EF-190: whole-batch precondition transport error
            // synthesizes skip rows for every remaining batch — without
            // this, downstream consumers (EF-193 banner + any future modal
            // banner keyed on the precondition marker) have no signal that
            // the PLACE batch was deliberately suppressed. Uses the same
            // action-mapping derivation as the per-order branch so the two
            // failure modes stay in lockstep.
            if (batchIsPrecondition) {
              allResults = allResults.concat(
                buildSkippedRowsForRemainingBatches(remainingBatches, batch.action)
              )
            }
            break
          }
        }
      } else {
        // Single-action payload (Move Stop, etc.)
        const filteredOrders = filterOrders(pendingPayload.orders || [])
        if (filteredOrders.length === 0) {
          setConfirmState(prev => ({ ...prev, loading: false }))
          clearTimeout(timeoutId)
          abortRef.current = null
          return
        }
        const apiAction = OMS_ACTION_MAP[pendingPayload.action] || pendingPayload.action
        const useSetupSubmit = pendingPayload.action === 'STAGE_ORDERS'
        if (useSetupSubmit) {
          const submittedAtMs = Date.now()
          allResults = await submitStagedSetupOrders(filteredOrders, apiAction)
          ensureAggregateTimeout()
          abortRef.current = controller
          allResults = await confirmBrokerState({
            batchAction: apiAction,
            orders: filteredOrders,
            results: allResults,
          })
          allResults = await recoverTimedOutSetupOrders({
            orders: filteredOrders,
            results: allResults,
            submittedAtMs,
            recoveryBatchAction: apiAction,
          })
        } else {
          ensureAggregateTimeout()
          const data = await sendWithSessionRetry({
            ...pendingPayload,
            action: apiAction,
            orders: filteredOrders,
          })
          if (data.results) {
            allResults = mergeBracketMetaIntoResults(filteredOrders, data.results)
            allResults = await confirmBrokerState({
              batchAction: apiAction,
              orders: filteredOrders,
              results: allResults,
            })
          } else {
            allResults = mergeBracketMetaIntoResults(
              filteredOrders,
              filteredOrders.map(o => ({
                account_number: o.account_number,
                success: false,
                error: data.error || 'Unknown error',
                ...(data.errorCode && { errorCode: data.errorCode }),
              }))
            )
          }
        }
      }

      // EF-189 v3: split-bracket partial-failure rollback. If a logical
      // bracket landed half-placed (one sibling succeeded, one failed),
      // issue a best-effort CANCEL on the successful sibling BEFORE
      // finalising results — a half-placed bracket with setup_status
      // 'failed' but a live target tranche at Schwab is a data-integrity
      // hole. Uses `existingOrderId` per _System/reference/oms-conventions.md
      // (using `orderId` would silently no-op at the OMS).
      if (splitGroupHasPartialFailure(allResults)) {
        const orphans = findSuccessfulOrphanSiblings(allResults)
        const rollbackOutcomes = await Promise.allSettled(orphans.map(async (orphan) => {
          const cancelPayload = {
            action: 'CANCEL',
            orders: [{
              account_number: orphan.account_number,
              symbol: orphan.symbol,
              orderType: 'CANCEL',
              existingOrderId: orphan.orderId,
            }],
          }
          try {
            const cancelData = await sendWithSessionRetry(cancelPayload)
            const ok = cancelData?.results?.[0]?.success === true
            return { orphan, ok }
          } catch {
            return { orphan, ok: false }
          }
        }))
        // EF-189 v3 post-Codex hardening: key rollback outcomes by
        // (account_number, orderId), not orderId alone. Schwab IDs are
        // unique in practice but defensive scoping prevents any
        // theoretical cross-account misattribution if two accounts ever
        // share an orderId value.
        const outcomeKey = (acct, id) => `${acct ?? ''}|${id}`
        const outcomeByKey = new Map()
        for (const settled of rollbackOutcomes) {
          if (settled.status !== 'fulfilled') continue
          const { orphan, ok } = settled.value
          outcomeByKey.set(outcomeKey(orphan.account_number, orphan.orderId), ok)
        }
        allResults = allResults.map(r => {
          if (r?.success !== true) return r
          if (r.orderId == null) return r
          const k = outcomeKey(r.account_number ?? r.accountNumber, r.orderId)
          if (!outcomeByKey.has(k)) return r
          const rollbackSuccess = outcomeByKey.get(k)
          if (rollbackSuccess) {
            return { ...r, rollbackAttempted: true, rollbackSuccess: true }
          }
          return {
            ...r,
            rollbackAttempted: true,
            rollbackSuccess: false,
            manualCleanupRequired: true,
            orphanedOrderId: r.orderId,
          }
        })
      }

      clearTimeout(timeoutId)
      abortRef.current = null
      setConfirmState(prev => ({ ...prev, loading: false, results: allResults }))

      const batchId = [
        'order-batch',
        pendingPayload.symbol || 'unknown',
        pendingPayload.action || 'UNKNOWN',
        Date.now(),
      ].join(':')
      reconcileOrderBatch({
        batchId,
        symbol: pendingPayload.symbol,
        action: pendingPayload.action,
        results: allResults,
      })

      if (allResults.every(r => r.success)) {
        setTimeout(() => {
          setConfirmState(prev => ({ ...prev, open: false }))
        }, 600)
      }

      // Auto-sync after any OMS action. EF-124: pass the per-order
      // result array so the callback can distinguish a fully-rejected
      // batch from a successful send (e.g. setup_status: 'failed' vs
      // 'working').
      if (onOrdersChanged) onOrdersChanged(allResults)

    } catch (err) {
      const wasUserCancel = err?.name === 'AbortError' && didUserCancelRef.current
      clearTimeout(timeoutId)
      abortRef.current = null
      if (wasUserCancel) return
      const errorMsg = err.name === 'AbortError' ? 'Request timed out (30s)' : err.message
      const failureResults = mergeBracketMetaIntoResults(
        fallbackOrders,
        fallbackOrders.map(o => ({
          account_number: o.account_number,
          success: false,
          error: errorMsg,
        }))
      )
      setConfirmState(prev => ({
        ...prev,
        loading: false,
        results: failureResults,
      }))

      const batchId = [
        'order-batch',
        pendingPayload.symbol || 'unknown',
        pendingPayload.action || 'UNKNOWN',
        Date.now(),
      ].join(':')
      reconcileOrderBatch({
        batchId,
        symbol: pendingPayload.symbol,
        action: pendingPayload.action,
        results: failureResults,
      })
      if (onOrdersChanged) onOrdersChanged(failureResults)
    }
  }, [pendingPayload, onOrdersChanged, biometricGate, reconcileOrderBatch])

  const cancelConfirmation = useCallback(() => {
    if (abortRef.current) {
      didUserCancelRef.current = true
      abortRef.current.abort()
      abortRef.current = null
    }
    setConfirmState({ open: false, action: null, symbol: null, orders: [], loading: false, results: null })
    setPendingPayload(null)
  }, [])

  return {
    omsEnabled,
    schwabAuthExpired,
    brokerConn,
    confirmState,
    showConfirmation,
    confirmOrder,
    cancelConfirmation,
  }
}
